import {
    AuthContext,
    DataTable,
    DataTableCell,
    DataTablePresenter,
    getOptions,
    IAuthContextProps,
    IFilterSchema,
    ITableColumn,
    ITableFetchOptions,
    LetterDetail,
    ListProviderLettersRequest,
    ProviderDisplayDocumentType,
    ProviderLetter,
    ProviderLetterSortType,
    renderEmpty,
    SupportLetterAddress,
} from '@enter/core';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { CellProps, Row } from 'react-table';
import { DialogContext, IDialogContextProps } from 'services';
import { IInjectedRootStore, IObservedRootStore, parseSortBy } from 'stores';
import * as S from './outbox.styles';

interface ILetterProps extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
}

interface ILetterState {
    loading: boolean;
    options: ITableFetchOptions | undefined;
}

const LetterAddressCell = ({ cell }: CellProps<SupportLetterAddress>): React.ReactElement => {
    const letterAddress = cell.value as SupportLetterAddress;

    if (!letterAddress) {
        return <>{renderEmpty()}</>;
    }

    return (
        <div>
            {letterAddress.name} <br />
            {_.compact([letterAddress.addressLineOne, letterAddress.addressLineTwo]).join(' ')} <br />
            {_.compact([letterAddress.city, letterAddress.state]).join(', ')} {letterAddress.zip}{' '}
            {letterAddress.country}
        </div>
    );
};

const ContactCell = ({ cell }: CellProps<SupportLetterAddress>): React.ReactElement => {
    const letterAddress = cell.value as SupportLetterAddress;

    if (!letterAddress) {
        return <>{renderEmpty()}</>;
    }

    const email = letterAddress.email ? `Email: ${letterAddress.email}` : undefined;
    const phone = letterAddress.phone ? `Phone: ${letterAddress.phone}` : undefined;

    const contact = _.compact([email, phone]).join('\n');

    return <div>{contact}</div>;
};

const columns: ITableColumn<ProviderLetter>[] = [
    {
        id: 'Id',
        Header: 'Id',
        accessor: 'id',
        disableSortBy: true,
        show: false,
    },
    {
        id: 'Company',
        Header: 'Company',
        disableSortBy: true,
        accessor: (row): string => _.get(row, 'response.to.company') || _.get(row, 'response.to.name'),
    },
    {
        id: 'Contact',
        Header: 'Contact',
        accessor: (row) => row.response?.to,
        disableSortBy: true,
        Cell: ContactCell,
    },
    {
        id: 'Address',
        Header: 'Address',
        accessor: (row) => row.response?.to,
        disableSortBy: true,
        Cell: LetterAddressCell,
    },
    {
        id: 'Created',
        Header: 'Created',
        accessor: 'created',
        sortType: ProviderLetterSortType.CREATED,
        defaultSort: { id: 'Created', desc: true },
        Cell: DataTableCell('DateTime'),
        show: false,
    },
];

const filterSchemas: IFilterSchema<keyof ListProviderLettersRequest>[] = [
    {
        label: 'Claim ID',
        filterId: 'filterClaimId',
        columnId: 'claimId',
        filterType: 'String',
    },
    {
        label: 'Document Id',
        filterId: 'filterDocumentId',
        columnId: 'documentId',
        filterType: 'String',
    },
    {
        label: 'Document Type',
        filterId: 'filterDocumentType',
        columnId: 'documentType',
        filterType: 'Select',
        options: getOptions(ProviderDisplayDocumentType),
    },
    {
        label: 'Created Date',
        filterId: 'filterCreated',
        filterType: 'DateTime',
        columnId: 'Created',
    },
];

class LetterPage extends React.Component<ILetterProps, ILetterState> {
    private presenter: DataTablePresenter<ProviderLetter>;

    constructor(props: ILetterProps) {
        super(props);
        this.state = {
            loading: false,
            options: undefined,
        };
        this.presenter = new DataTablePresenter<ProviderLetter>({
            name: 'letter',
            data: [],
            columns,
            filterSchemas,
        });
    }

    public componentDidMount(): void {
        document.title = 'ENTER Platform - Letter';
    }
    public componentWillUnmount(): void {
        this.authContext.cancelGroup('letters');
    }

    public render(): React.ReactElement {
        return (
            <DataTable
                presenter={this.presenter}
                isLoading={this.state.loading}
                onFetch={this.onFetch}
                onRowClick={this.onRowClick}
                renderRowSubComponent={this.renderRowSubComponent}
            />
        );
    }

    private get authContext(): IAuthContextProps {
        return this.props.authContext;
    }

    private get dialogContext(): IDialogContextProps {
        return this.props.dialogContext;
    }

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        if (!_.isEqual(this.state.options, fetchOptions)) {
            try {
                this.setState({
                    options: fetchOptions,
                    loading: true,
                });
                this.authContext.cancelGroup('letters');
                this.authContext.setGroup('letters');

                const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
                const sortAndOrder = parseSortBy(sortBy, columns);
                const letterListing = await this.props.rootStore.claimStore.loadLetterListing({
                    page: pageIndex,
                    pageSize,
                    ...sortAndOrder,
                    filters: validFilters,
                });
                this.presenter.onDataUpdate(letterListing.items || []);
                this.presenter.onDataTotalChange(letterListing.total || 0);
                this.setState({
                    loading: false,
                });
            } catch (err) {
                this.dialogContext.showError(err);
            }
        }
    };

    private onRowClick = async (row: Row<ProviderLetter>): Promise<void> => {
        // retrieve letter cart if necessary
        row.toggleRowExpanded();
    };

    private renderRowSubComponent = (row: Row<ProviderLetter>): React.ReactElement => {
        const LetterDetailWrapper = () => {
            const [loading, setLoading] = React.useState(false);
            const [documentUrl, setDocumentUrl] = React.useState<string | undefined>();

            React.useEffect(() => {
                (async (): Promise<void> => {
                    const letter = row.original;
                    if (letter?.id && letter.document?.id) {
                        setLoading(true);
                        try {
                            const url = await this.props.rootStore!.claimStore.viewLetterById(letter.id);
                            setDocumentUrl(url?.url);
                        } catch (error) {
                            console.error('Error loading letter document:', error);
                        } finally {
                            setLoading(false);
                        }
                    }
                })();
            }, []);

            return <LetterDetail letter={row.original} appType="provider" documentUrl={documentUrl} loading={loading} />;
        };

        return (
            <S.Expand>
                <LetterDetailWrapper />
            </S.Expand>
        );
    };
}

export const OutboxMail: React.FC<IObservedRootStore> = inject('rootStore')(
    observer(
        (props: IObservedRootStore): React.ReactElement => {
            const { rootStore } = props;
            if (!rootStore) {
                return <></>;
            }
            const authContext = useContext(AuthContext);
            const dialogContext = useContext(DialogContext);
            return <LetterPage authContext={authContext} dialogContext={dialogContext} rootStore={rootStore} />;
        },
    ),
);
